import { useState, useEffect, useCallback, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./components/Login";
import Landing from "./components/Landing";
import Dashboard from "./components/Dashboard";
import ResetPassword from "./components/ResetPassword";
import Report from "./components/Report";
import Notes from "./components/Notes";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import { Toaster } from "./components/ui/toaster";
import { authAPI, tasksAPI } from "./services/api";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#333' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#888', marginTop: '10px' }}>{this.state.error?.message}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Apply theme-based settings
  useEffect(() => {
    const applyThemeScale = () => {
      const theme = localStorage.getItem('theme') || 'prime';
      // Theme class no longer needed on html root
      // Task card styling is handled by .task-card-inkpaper class in CSS
    };
    
    applyThemeScale();
    window.addEventListener('storage', applyThemeScale);
    
    return () => {
      window.removeEventListener('storage', applyThemeScale);
    };
  }, []);

  const processGoogleOAuth = useCallback(async (sessionId) => {
    if (!sessionId || sessionId.trim() === '') {
      setLoading(false);
      return;
    }
    
    try {
      const data = await authAPI.processGoogleSession(sessionId);
      window.history.replaceState(null, '', window.location.pathname);
      
      const localTasks = localStorage.getItem('taskapp_tasks');
      if (localTasks) {
        try {
          const tasks = JSON.parse(localTasks);
          if (tasks.length > 0) {
            await tasksAPI.migrateTasks(tasks);
            localStorage.removeItem('taskapp_tasks');
          }
        } catch (err) {
          console.error('Task migration failed:', err);
        }
      }
      
      setUser(data.user);
    } catch (error) {
      console.error('Google OAuth processing failed:', error);
      localStorage.removeItem('taskapp_user');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('taskapp_user');
      if (storedUser) {
        const currentUser = await authAPI.getMe();
        setUser(currentUser);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('taskapp_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    
    let sessionId = null;
    
    if (hash && hash.includes('session_id=')) {
      sessionId = hash.split('session_id=')[1].split('&')[0];
    }
    
    if (!sessionId && search && search.includes('session_id=')) {
      sessionId = new URLSearchParams(search).get('session_id');
    }
    
    if (sessionId) {
      processGoogleOAuth(sessionId);
      return;
    }
    
    checkAuth();
  }, [processGoogleOAuth, checkAuth]);

  const handleLogout = () => {
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Landing />
              )
            }
          />
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Login onLogin={setUser} />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user ? (
                <ErrorBoundary><Dashboard username={user.name || user.email} onLogout={handleLogout} /></ErrorBoundary>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/reset-password/:token"
            element={<ResetPassword />}
          />
          <Route
            path="/report"
            element={<Report />}
          />
          <Route
            path="/notes"
            element={<Notes />}
          />
          <Route
            path="/privacy"
            element={<Privacy />}
          />
          <Route
            path="/terms"
            element={<Terms />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
